import Image from "next/image";

import cn from "classnames";
import { Image as ImageType } from "sanity";

import { ChevronRightIcon } from "@/components/icons/chevronRight.icon";
import { QueryLink } from "@/components/queryLink/queryLink.component";
import { RichText } from "@/components/richText/richText.component";
import { Text } from "@/components/text/text.component";

import { PRODUCT_DRAWER_QUERY_PARAM } from "@/constants/product.constants";
import { RichText as RichTextType } from "@/sanity.types";
import { urlFor } from "@/sanity/utils";

export const ConfiguratorHeaderProduct = ({
  productId,
  title,
  subtitle,
  image,
  imageAlt,
  minimal = false,
  showViewDetailsLink,
  ...rest
}: ConfiguratorHeaderProps) => {
  let imageUrl;
  try {
    imageUrl = image ? urlFor(image).width(260).height(260).url() : null;
  } catch {
    console.warn("error loading configurator base package image", { image });
  }
  const imageSize = minimal ? 56 : 144;
  return (
    <li
      data-testid="configurator-base-package-product"
      className={cn("flex flex-1 items-center gap-4 rounded-lg p-4 lg:gap-6", {
        "bg-white": !minimal,
      })}
      {...rest}>
      <div
        className={cn("flex items-center justify-center overflow-hidden", {
          "h-20 w-20 lg:h-36 lg:w-36": !minimal,
          "h-14 w-14": minimal,
        })}
        data-testid="configurator-base-package-product-image">
        {imageUrl ? (
          <Image
            src={imageUrl}
            alt={imageAlt ?? ""}
            width={imageSize}
            height={imageSize}
          />
        ) : null}
      </div>
      <div className="flex flex-1 flex-col gap-1">
        <Text
          data-testid="configurator-base-package-product-title"
          variant="body2">
          {title}
        </Text>
        <RichText data-testid="configurator-base-package-product-short-description">
          {subtitle}
        </RichText>
        {productId && showViewDetailsLink ? (
          <QueryLink
            set={{
              [PRODUCT_DRAWER_QUERY_PARAM]: productId,
            }}
            data-testid="configurator-base-package-product-view-details"
            data-product-id={productId}
            className="mt-2 flex gap-1"
            aria-label="View details (opens a dialog)">
            <Text className="border-adt-black pointer-events-none border-b">
              View details
            </Text>
            <ChevronRightIcon className="pointer-events-none" width={14} />
          </QueryLink>
        ) : null}
      </div>
    </li>
  );
};

export type ConfiguratorHeaderProps = {
  productId?: string;
  title?: string;
  subtitle?: RichTextType;
  image?: ImageType;
  imageAlt?: string;
  minimal?: boolean;
  showViewDetailsLink: boolean;
};
